import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/layout/Sidebar';
import PageContainer from '../../components/layout/PageContainer';
import { useAuth } from '../../hooks/useAuth';
import EngineersView from './EngineersView';
import TicketManagementView from './TicketManagementView';
import SettingsView from './SettingsView';
import { SiteProvider, useSite } from '../../contexts/SiteContext';
import SiteSelector from '../../components/common/SiteSelector';
import contractService from '../../services/contractService';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import PersonalDashboardView from './PersonalDashboardView';
import MyTicketsView from './MyTicketsView';
import WorkReportView from './WorkReportView';
import ScheduleView from './ScheduleView';
import KnowledgeBaseView from './KnowledgeBaseView';

const TLDashboardContent = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { sites, selectedSite, setSelectedSite, loadingSites } = useSite();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeContract, setActiveContract] = useState(null);
  const [isLoadingContract, setIsLoadingContract] = useState(false);
  const [prefilledWorkReportTicket, setPrefilledWorkReportTicket] = useState(null);
  const [ticketFilter, setTicketFilter] = useState('');

  const handleOpenWorkReportFromTicket = (ticket) => {
    if (!ticket?.id) return;
    setPrefilledWorkReportTicket(ticket);
    navigate('/tl/reports');
  };

  useEffect(() => {
    const fetchContract = async () => {
      if (selectedSite && selectedSite !== 0) {
        setIsLoadingContract(true);
        const result = await contractService.getAllContracts(selectedSite);
        if (result.success && result.data.length > 0) {
          // Get the active one or the most recent one
          const active = result.data.find(c => c.is_enabled) || result.data[0];
          setActiveContract(active);
        } else {
          setActiveContract(null);
        }
        setIsLoadingContract(false);
      } else {
        setActiveContract(null);
      }
    };

    fetchContract();
  }, [selectedSite]);

  const pages = [
    { id: 'engineers', name: 'Engineers', icon: 'engineers' },
    { id: 'ticket-management', name: 'Ticket Management', icon: 'ticketManagement' },
    { id: 'dashboard', name: 'Personal Dashboard', icon: 'dashboard' },
    { id: 'tickets', name: 'My Tickets', icon: 'tickets' },
    { id: 'reports', name: 'Work Reports', icon: 'reports' },
    { id: 'schedule', name: 'My Schedule', icon: 'scheduleManagement' },
    { id: 'knowledge', name: 'Knowledge Base', icon: 'knowledge' },
    { id: 'settings', name: 'Settings', icon: 'settings' }
  ];

  const containerStyle = {
    display: 'flex',
    minHeight: '100vh',
    background: 'var(--background)'
  };

  const pageSegment = location.pathname.split('/')[2] || 'engineers';
  const currentPage = pages.some((page) => page.id === pageSegment) ? pageSegment : 'engineers';

  const setCurrentPage = (pageId) => {
    setTicketFilter('');
    navigate(`/tl/${pageId}`);
  };

  const navigateToMyTickets = (filter = '') => {
    setTicketFilter(filter);
    navigate('/tl/tickets');
  };

  const mainContentStyle = {
    flex: 1,
    marginLeft: sidebarOpen ? '260px' : '80px',
    transition: 'margin-left 0.3s ease',
    minHeight: '100vh'
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'engineers':
        return <EngineersView />;
      case 'ticket-management':
        return <TicketManagementView />;
      case 'dashboard':
        return <PersonalDashboardView onStartWorkNavigate={handleOpenWorkReportFromTicket} navigateToMyTickets={navigateToMyTickets}/>;
      case 'tickets':
        return <MyTicketsView onStartWorkNavigate={handleOpenWorkReportFromTicket} defaultFilter={ticketFilter}/>;
      case 'reports':
        return (
          <WorkReportView
            prefilledTicket={prefilledWorkReportTicket}
            onPrefillConsumed={() => setPrefilledWorkReportTicket(null)}
          />
        );
      case 'schedule':
        return <ScheduleView />;
      case 'knowledge':
        return <KnowledgeBaseView />;
      case 'settings':
        return <SettingsView />;
      default:
        return <EngineersView />;
    }
  };

  const getPageTitle = () => {
    const page = pages.find((p) => p.id === currentPage);
    return page?.name || 'Team Lead Dashboard';
  };

  const getPageSubtitle = () => {
    const subtitles = {
      engineers: 'Manage engineers and their workload',
      'ticket-management': 'Track and progress team tickets',
      dashboard: 'Your personal worker performance overview',
      tickets: 'Manage your self-assigned tickets',
      reports: 'Submit and manage your work completion reports',
      schedule: 'View-only list of your scheduled tickets',
      knowledge: 'Resources and guides for field work',
      settings: 'Update your account settings'
    };

    return subtitles[currentPage] || '';
  };

  const isTLManagementView = ['engineers', 'ticket-management'].includes(currentPage);

  return (
    <div style={containerStyle}>
      <Sidebar
        user={user}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        pages={pages}
        onLogout={logout}
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
      />
      <div style={mainContentStyle}>
        <PageContainer title={getPageTitle()} subtitle={getPageSubtitle()}>
          {isTLManagementView && (
            <Card marginBottom="24px" padding="24px">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {sites.length > 1 && (
                  <SiteSelector
                    sites={sites}
                    selectedSite={selectedSite}
                    onChange={setSelectedSite}
                    loading={loadingSites}
                    noMargin // I'll add this prop to SiteSelector if needed, or just let it be
                  />
                )}

                {selectedSite !== 0 && (
                  <div style={{ 
                    paddingTop: '20px', 
                    borderTop: '1px solid var(--border)',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '24px'
                  }}>
                    {/* Site Details */}
                    <div>
                      <h4 style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '12px', letterSpacing: '0.05em' }}>Site Details</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div>
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Address</div>
                          <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)', lineHeight: '1.4' }}>
                            {sites.find(s => s.id === selectedSite)?.address || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Contract Details */}
                    {isLoadingContract ? (
                      <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Loading contract details...</div>
                    ) : activeContract ? (
                      <>
                        <div>
                          <h4 style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '12px', letterSpacing: '0.05em' }}>Contract Info</h4>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <div>
                              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>PO Number</div>
                              <div style={{ fontSize: '14px', fontWeight: '600' }}>{activeContract.po_number}</div>
                            </div>
                            <div>
                              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Type</div>
                              <Badge variant={activeContract.type === 'Comprehensive' ? 'success' : 'info'}>
                                {activeContract.type}
                              </Badge>
                            </div>
                            <div style={{ gridColumn: 'span 2' }}>
                              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Validity</div>
                              <div style={{ fontSize: '14px', fontWeight: '500' }}>
                                {new Date(activeContract.start_date).toLocaleDateString()} - {new Date(activeContract.end_date).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Services */}
                        <div style={{ gridColumn: 'span 2' }}>
                          <h4 style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '12px', letterSpacing: '0.05em' }}>Included Services</h4>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            {activeContract.services?.comprehensive?.length > 0 && (
                              <div>
                                <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--success)', marginBottom: '8px' }}>Comprehensive</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                  {activeContract.services.comprehensive.map(s => (
                                    <span key={s} style={{ fontSize: '12px', background: 'var(--success-light)', color: 'var(--success)', padding: '4px 8px', borderRadius: '4px' }}>{s}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {activeContract.services?.non_comprehensive?.length > 0 && (
                              <div>
                                <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--info)', marginBottom: '8px' }}>Non-Comprehensive</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                  {activeContract.services.non_comprehensive.map(s => (
                                    <span key={s} style={{ fontSize: '12px', background: 'var(--info-light)', color: 'var(--info)', padding: '4px 8px', borderRadius: '4px' }}>{s}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div style={{ color: 'var(--text-secondary)', fontSize: '14px', fontStyle: 'italic' }}>No active contract found for this site.</div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          )}
          {renderPage()}
        </PageContainer>
      </div>
    </div>
  );
};

const TLDashboard = () => {
  return (
    <SiteProvider>
      <TLDashboardContent />
    </SiteProvider>
  );
};

export default TLDashboard;
